import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { CATEGORY_META, CATEGORY_ORDER, T, tx } from "@/lib/i18n";
import { PostCard, SectionHeader } from "@/components/PostCard";
import { Search, Sparkles, ShieldCheck, Zap, ArrowRight } from "lucide-react";

const SECTION_ORDER = ["jobs", "results", "admit-card", "answer-key", "syllabus", "admissions", "scholarships", "schemes"];

export default function Home() {
  const { lang } = useLang();
  const [buckets, setBuckets] = useState({});
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const results = await Promise.all(
        SECTION_ORDER.map((c) => api.get(`/posts/latest?category=${c}&limit=6`).then((r) => [c, r.data.items]))
      );
      setBuckets(Object.fromEntries(results));
      const f = await api.get(`/posts?featured=true&limit=4`).then((r) => r.data.items);
      setFeatured(f);
    })();
  }, []);

  const submit = (e) => {
    e.preventDefault();
    nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200" data-testid="home-hero">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-slate-50 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-12 sm:pb-20 grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-orange-600 bg-orange-50 border border-orange-100 px-2.5 py-1 rounded-sm mb-6" data-testid="hero-eyebrow">
              <Sparkles size={12} /> {lang === "hi" ? "एक भरोसेमंद स्रोत" : "One trusted feed"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]">
              {lang === "hi" ? (
                <>सरकारी नौकरी की <span className="text-orange-600">हर सूचना</span>, एक जगह।</>
              ) : (
                <>Every Indian <span className="text-orange-600">Govt. Job</span> update, in one clean feed.</>
              )}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              {T.tagline[lang]}
            </p>
            <form onSubmit={submit} className="mt-8 flex max-w-xl border border-slate-300 rounded-md bg-white focus-within:border-slate-900 focus-within:ring-2 focus-within:ring-slate-900/10 transition-colors">
              <div className="pl-4 grid place-items-center text-slate-400">
                <Search size={18} />
              </div>
              <input
                data-testid="hero-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={tx("ui.search_placeholder", lang)}
                className="flex-1 h-14 px-3 text-sm outline-none bg-transparent"
              />
              <button
                data-testid="hero-search-btn"
                type="submit"
                className="m-1.5 px-5 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors inline-flex items-center gap-1.5"
              >
                {lang === "hi" ? "खोजें" : "Search"} <ArrowRight size={14} />
              </button>
            </form>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-xl">
              {[
                { icon: Zap, en: "Fastest updates", hi: "तेज़ अपडेट" },
                { icon: ShieldCheck, en: "Verified links only", hi: "प्रामाणिक लिंक" },
                { icon: Sparkles, en: "EN + हिंदी", hi: "EN + हिंदी" },
              ].map(({ icon: Icon, en, hi }) => (
                <div key={en} className="flex items-center gap-2 text-xs text-slate-600">
                  <div className="h-8 w-8 grid place-items-center bg-slate-900 text-white rounded-sm">
                    <Icon size={14} />
                  </div>
                  <span className="font-medium">{lang === "hi" ? hi : en}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 relative">
            <div className="bg-slate-900 text-white rounded-md p-6 sm:p-7 relative overflow-hidden">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-orange-400 mb-3">
                {lang === "hi" ? "आज की मुख्य खबरें" : "Featured Today"}
              </div>
              <ul className="space-y-3.5">
                {featured.length === 0 && (
                  <li className="text-sm text-slate-400">Loading…</li>
                )}
                {featured.slice(0, 4).map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/post/${p.slug}`}
                      data-testid={`featured-${p.slug}`}
                      className="group flex items-start justify-between gap-3 py-2 border-b border-white/10 last:border-0 hover:border-orange-400/50 transition-colors"
                    >
                      <div>
                        <div className="text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-1">
                          {CATEGORY_META[p.category][lang === "hi" ? "short_hi" : "short_en"]}
                        </div>
                        <div className="text-sm font-medium leading-snug line-clamp-2 group-hover:text-orange-400 transition-colors">
                          {(lang === "hi" && p.title_hi) ? p.title_hi : p.title_en}
                        </div>
                      </div>
                      <ArrowRight size={14} className="mt-1 flex-shrink-0 text-slate-500 group-hover:text-orange-400 transition-colors" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY QUICK GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-b border-slate-200">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {CATEGORY_ORDER.map((slug) => (
            <Link
              key={slug}
              to={`/${slug}`}
              data-testid={`cat-tile-${slug}`}
              className="card-hover text-center bg-white border border-slate-200 rounded-md p-4"
            >
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
                {CATEGORY_META[slug].short_en}
              </div>
              <div className="font-display font-semibold text-sm text-slate-900">
                {CATEGORY_META[slug][lang === "hi" ? "hi" : "en"]}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CATEGORY SECTIONS */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-14">
        {SECTION_ORDER.map((slug) => {
          const items = buckets[slug] || [];
          if (items.length === 0) return null;
          const meta = CATEGORY_META[slug];
          return (
            <section key={slug} data-testid={`home-section-${slug}`}>
              <SectionHeader
                subtitle={lang === "hi" ? "नवीनतम" : "Latest"}
                title={meta[lang === "hi" ? "hi" : "en"]}
                viewAllHref={`/${slug}`}
                testId={`view-all-${slug}`}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((p) => (
                  <PostCard key={p.id} post={p} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
